import { ImageScaleView, View, ViewOpts } from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Match2TournamentPrize } from './Match2TournamentPrize';
import { Match2TournamentScore } from './Match2TournamentScore';
import { TournamentType } from 'src/replicant/match2/tournament/match2-tournament.ruleset';
import * as match2TournamentGetters from 'src/replicant/match2/tournament/match2-tournament.getters';
import { AvatarFrameId } from 'src/replicant/profile/frames';

export type Match2TournamentEntryParams = {
  id: string;
  name: string;
  position: number;
  score: number;
  scoreUpdatedAt: number;
  avatar: string;
  frame: AvatarFrameId;
  me?: boolean;
  alt?: boolean;
};

export class Match2TournamentEntry extends View {
  private readonly bg: ImageScaleView;
  private readonly positionText: LangBitmapFontTextView;
  private readonly positionIcon: ImageScaleView;
  private readonly avatar: Avatar;
  private readonly name: LangBitmapFontTextView;
  private readonly score: Match2TournamentScore;
  private readonly prize: Match2TournamentPrize;
  private readonly left: View;
  private readonly right: View;
  private readonly type: TournamentType;

  constructor(opts: ViewOpts, type: TournamentType = 'star') {
    super({ width: 580, height: 70, ...opts });

    this.type = type;

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height + 7,
      image: 'assets/ui/match2/common/list_entry.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 22, right: 22 },
        vertical: { top: 22, bottom: 22 },
      },
    });

    this.left = new View({
      superview: this,
    });

    this.right = new View({
      superview: this,
    });

    this.name = new LangBitmapFontTextView({
      superview: this.left,
      x: 140,
      y: 0,
      align: 'left',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: 150,
      height: this.style.height,
      color: '#754111',
    });

    this.positionText = new LangBitmapFontTextView({
      superview: this.left,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Title',
      width: this.style.height,
      height: this.style.height,
    });

    this.positionIcon = new ImageScaleView({
      superview: this.left,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      width: 62,
      height: 62,
    });

    this.avatar = new Avatar({
      superview: this.left,
      width: 58,
      height: 58,
      x: 105,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
    });

    this.score = new Match2TournamentScore(
      {
        superview: this.right,
        x: this.style.width - 130,
        y: 0,
        width: 80,
        height: this.style.height,
      },
      type,
    );

    this.prize = new Match2TournamentPrize({
      superview: this.right,
      x: this.style.width - 270,
      y: 10,
      width: 130,
      height: 60,
    });
  }

  public setup(params: Match2TournamentEntryParams) {
    const alt = params.me || params.alt;
    let name = params.name;

    this.name.localeText = () => name;
    this.name.updateOpts({
      color: alt ? '#FFFFFF' : '#754111',
      font: alt ? 'Title' : 'Body',
    });
    this.score.setup({ score: params.score });

    if (params.position >= 1 && params.position <= 3) {
      this.positionIcon.updateOpts({
        image: `assets/ui/match2/tournament/star_tournament_medal_${params.position}.png`,
        visible: true,
      });
      this.positionText.updateOpts({ visible: false });
    } else {
      this.positionText.updateOpts({ visible: true });
      this.positionIcon.updateOpts({ visible: false });
      this.positionText.localeText = () => params.position.toString();
    }

    this.avatar.updateImage(params.avatar);
    this.avatar.setFrame(params.frame);

    const prizes = match2TournamentGetters.getRewardsByRank(params.position);

    if (prizes.length > 0) {
      this.prize.updateOpts({ visible: true });
      this.prize.setup(prizes);
    } else {
      this.prize.updateOpts({ visible: false });
    }

    if (params.alt) {
      this.prize.updateOpts({ offsetY: 12 });
      this.left.updateOpts({ offsetX: 10 });
      this.right.updateOpts({ offsetX: -10 });
      this.bg.updateOpts({
        offsetY: -7,
        image: `assets/ui/match2/common/list_entry_alt_${
          this.type === 'streak' ? 'blue' : 'red'
        }.png`,
      });
    } else {
      this.prize.updateOpts({ offsetY: 0 });
      this.left.updateOpts({ offsetX: 0 });
      this.right.updateOpts({ offsetX: 0 });
      this.bg.updateOpts({
        offsetY: 0,
        image: 'assets/ui/match2/common/list_entry.png',
      });
    }

    if (params.me) {
      this.bg.updateOpts({
        image: `assets/ui/match2/common/list_entry_${
          this.type === 'streak' ? 'blue' : 'red'
        }.png`,
      });
    } else {
      this.bg.updateOpts({
        opacity: 1,
      });
    }
  }
}
